import { useEffect, useMemo, useState } from "react";

import { useSession } from "./auth-client";
import { trpc } from "./trpc";
import { cachedQuery } from "./cached-query";

// BetterAuth's `useSession()` only knows about its own session cookie. The
// API additionally accepts a dev-bypass cookie (set by dev.loginAsTestUser)
// that resolves through tRPC's context. This hook layers a fallback
// `user.whoami` query on top of useSession so a dev-cookie user is
// recognized everywhere `useSession()` is used. In production the fallback
// just 401s quickly, so the round-trip cost is paid only on logged-out
// visits.

export interface EffectiveUser {
  id: string;
  email: string;
  name: string | null;
  emailVerified: boolean;
}

export interface EffectiveSession {
  isPending: boolean;
  user: EffectiveUser | null;
}

export function useEffectiveSession(): EffectiveSession {
  const session = useSession();
  const [fallbackUser, setFallbackUser] = useState<EffectiveUser | null>(null);
  const [fallbackPending, setFallbackPending] = useState(false);

  const baUser = session.data?.user;

  useEffect(() => {
    // Guard against setState after unmount or after this effect run is
    // superseded (session resolves / baUser changes mid-flight). Without
    // it the in-flight whoami can land on a stale/unmounted component and
    // strand `fallbackPending` at true.
    let cancelled = false;
    if (session.isPending) return;
    if (baUser) {
      setFallbackUser(null);
      // BetterAuth now owns the session — clear any pending fallback so a
      // prior logged-out run's spinner doesn't stay stuck on.
      setFallbackPending(false);
      return;
    }
    setFallbackPending(true);
    void (async () => {
      try {
        const me = await cachedQuery("user.whoami", () =>
          trpc.user.whoami.query(),
        );
        if (cancelled) return;
        setFallbackUser({
          id: me.id,
          email: me.email,
          name: me.name,
          emailVerified: me.emailVerified,
        });
      } catch {
        if (cancelled) return;
        setFallbackUser(null);
      } finally {
        if (!cancelled) setFallbackPending(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session.isPending, baUser?.id]);

  // Stabilize the returned user's object identity. BetterAuth's
  // `useSession()` hands back a fresh `user` object every render, so
  // returning a new literal here would change identity on every render —
  // any consumer that lists `session.user` in a hook dep array (e.g. the
  // practice loader's useEffect) would then re-run on every render,
  // refetching and visibly swapping its data (the question/answer flicker).
  // Memoizing on the primitive fields keeps the reference stable until the
  // underlying user actually changes.
  const baId = baUser?.id;
  const baEmail = baUser?.email;
  const baName = baUser?.name ?? null;
  const baVerified = baUser?.emailVerified ?? false;
  const user = useMemo<EffectiveUser | null>(() => {
    if (baId && baEmail !== undefined) {
      return {
        id: baId,
        email: baEmail,
        name: baName,
        emailVerified: baVerified,
      };
    }
    return fallbackUser;
  }, [baId, baEmail, baName, baVerified, fallbackUser]);

  return {
    isPending: baUser ? false : session.isPending || fallbackPending,
    user,
  };
}
